/**
 * elyon-speed.js — Elyon Dubai Performance Optimizer v2
 * Fixes: HD video preloading, eager images, third-party DNS prefetch
 * Deployed: 15 Apr 2026
 */
(function () {
  'use strict';

  /* ── 0. URL PATH NORMALISATION ────────────────────────────────────────────
     Shopify serves a 200 for paths with repeated slashes, e.g.
     /products/x, //products/x and ///products/x all render the same page.
     That is platform behaviour and cannot be redirected server side on
     Shopify (admin URL Redirects are exact match only, and there are
     infinite slash permutations).

     SEO is already safe: Shopify emits the correct rel=canonical and
     og:url on those pages. This only tidies the address bar for real
     visitors and keeps path based rules (analytics filters, ad platform
     rules, redirects) matching a single canonical path.

     Guard rails: only acts when the path really contains a repeated
     slash, only ever shortens the path, and bails out if the result is
     unchanged, so it can never loop. */
  try {
    var rawPath = window.location.pathname;
    if (rawPath.indexOf('//') !== -1) {
      var cleanPath = rawPath.replace(/\/{2,}/g, '/');
      if (cleanPath && cleanPath !== rawPath && cleanPath.charAt(0) === '/') {
        window.location.replace(cleanPath + window.location.search + window.location.hash);
        return;
      }
    }
  } catch (e) { /* never let this block the rest of the file */ }

  /* ── 1. PRECONNECT HINTS ──────────────────────────────────────────────────
     Add DNS prefetch + preconnect for all identified slow third-party domains
     before the browser discovers them naturally — saves 150-500ms per domain */
  var hints = [
    ['preconnect',   'https://static.klaviyo.com'],
    ['preconnect',   'https://static-tracking.klaviyo.com'],
    ['dns-prefetch', 'https://cdn.aggle.net'],
    ['preconnect',   'https://scripts.clarity.ms'],
    ['preconnect',   'https://www.googletagmanager.com'],
    ['dns-prefetch', 'https://satcb.azureedge.net'],
    ['dns-prefetch', 'https://cdn.zigpoll.com'],
    ['dns-prefetch', 'https://shop.app'],
    ['dns-prefetch', 'https://connect.facebook.net'],
    ['dns-prefetch', 'https://widget.trustpilot.com'],
    ['dns-prefetch', 'https://invitejs.trustpilot.com'],
    ['dns-prefetch', 'https://ecommplugins-scripts.trustpilot.com'],
    ['dns-prefetch', 'https://api.socialsnowball.io'],
    ['dns-prefetch', 'https://pixel.wetracked.io']
  ];
  var frag = document.createDocumentFragment();
  hints.forEach(function (h) {
    var el = document.createElement('link');
    el.rel = h[0];
    el.href = h[1];
    if (h[0] === 'preconnect') el.crossOrigin = '';
    frag.appendChild(el);
  });
  document.head.appendChild(frag);

  /* ── 2. VIDEO LAZY LOADING (Swiper-aware) ────────────────────────────────
     Product gallery uses Swiper.js — all slides are in the DOM but only one
     is active (.swiper-slide-active). Only that slide's video should preload.
     All other gallery videos get preload=none until the user swipes to them.

     Also handles non-swiper videos (sticky banners etc.) via viewport check. */
  function getSwiperSlide(el) {
    var cur = el;
    for (var i = 0; i < 8; i++) {
      cur = cur.parentElement;
      if (!cur) return null;
      if (cur.classList && cur.classList.contains('swiper-slide')) return cur;
    }
    return null;
  }

  function deferVideo(v) {
    v.dataset.elyonAutoplay = v.hasAttribute('autoplay') ? 'true' : 'false';
    v.setAttribute('preload', 'none');
    v.removeAttribute('autoplay');
    try { v.pause(); } catch(e) {}
  }

  function restoreVideo(v) {
    v.setAttribute('preload', 'metadata');
    if (v.dataset.elyonAutoplay === 'true') {
      v.setAttribute('autoplay', '');
      try { v.play(); } catch(e) {}
    }
  }

  function optimizeVideos() {
    var videos = Array.prototype.slice.call(document.querySelectorAll('video'));
    if (!videos.length) return;

    /* IntersectionObserver to restore deferred videos when they scroll into view */
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) {
          restoreVideo(e.target);
          io.unobserve(e.target);
        }
      });
    }, { rootMargin: '400px 0px' });

    /* Also watch swiper for slide changes and restore the new active slide's video */
    function watchSwiperSlideChange() {
      document.addEventListener('swiperslidechange', function (e) {
        var activeSlide = document.querySelector('.swiper-slide-active');
        if (!activeSlide) return;
        var v = activeSlide.querySelector('video');
        if (v && v.preload === 'none') restoreVideo(v);
      });
      /* Fallback: MutationObserver on swiper-slide-active class changes */
      var mo = new MutationObserver(function (mutations) {
        mutations.forEach(function (m) {
          if (m.type === 'attributes' && m.attributeName === 'class') {
            var el = m.target;
            if (el.classList.contains('swiper-slide-active')) {
              var v = el.querySelector('video');
              if (v && v.preload === 'none') restoreVideo(v);
            }
          }
        });
      });
      document.querySelectorAll('.swiper-slide').forEach(function (slide) {
        mo.observe(slide, { attributes: true });
      });
    }

    var vpHeight = window.innerHeight || 800;

    videos.forEach(function (v, idx) {
      var slide = getSwiperSlide(v);

      if (slide) {
        /* Inside swiper gallery — only active slide preloads */
        if (slide.classList.contains('swiper-slide-active')) {
          /* Active slide: allow full preload */
          v.setAttribute('preload', 'metadata');
        } else {
          /* Inactive slide: defer completely */
          deferVideo(v);
          io.observe(v);
        }
      } else {
        /* Not in swiper — use viewport check */
        var rect = v.getBoundingClientRect();
        var inViewport = rect.top < vpHeight && rect.bottom > 0;
        if (!inViewport) {
          deferVideo(v);
          io.observe(v);
        } else {
          /* Above-fold non-swiper video: keep first one, defer rest */
          if (idx > 0) {
            deferVideo(v);
            io.observe(v);
          } else {
            v.setAttribute('preload', 'metadata');
          }
        }
      }
    });

    watchSwiperSlideChange();
  }

  /* ── 3. IMAGE LOADING FIX ────────────────────────────────────────────────
     - Mark the first large above-fold image as fetchpriority=high (LCP)
     - Lazy-load any remaining eager above-fold images that are not LCP */
  function optimizeImages() {
    var imgs = Array.prototype.slice.call(document.querySelectorAll('img'));
    var lcpSet = false;
    var vpHeight = window.innerHeight || 800;

    imgs.forEach(function (img) {
      var rect = img.getBoundingClientRect();
      var inView = rect.top < vpHeight && rect.bottom > 0;
      var meaningful = img.naturalWidth > 80 || img.offsetWidth > 80;

      if (!lcpSet && inView && meaningful) {
        img.setAttribute('fetchpriority', 'high');
        img.removeAttribute('loading');
        lcpSet = true;
        return;
      }
      if (!inView && img.loading !== 'lazy' && !img.complete) {
        img.setAttribute('loading', 'lazy');
      }
    });
  }

  /* ── 4. IFRAME OPTIMISATION ──────────────────────────────────────────────
     Tiny invisible iframes (tracking pixels) get forced lazy */
  function optimizeIframes() {
    Array.prototype.slice.call(document.querySelectorAll('iframe')).forEach(function (f) {
      if (f.offsetWidth < 10 || f.offsetHeight < 10) {
        f.setAttribute('loading', 'lazy');
      }
    });
  }

  /* ── 5. DUPLICATE CLARITY BLOCK ──────────────────────────────────────────
     Clarity loads from TWO domains on this site (scripts.clarity.ms + clarity.ms).
     Block the second initialisation — it wastes 7+ seconds of network bandwidth. */
  var _clarityInits = 0;
  var _clarityFn = window.clarity;
  try {
    Object.defineProperty(window, 'clarity', {
      get: function () { return _clarityFn; },
      set: function (fn) {
        _clarityInits++;
        if (_clarityInits <= 1) { _clarityFn = fn; }
        /* silently drop second init */
      },
      configurable: true
    });
  } catch(e) { /* defineProperty not supported — skip */ }

  /* ── RUN ─────────────────────────────────────────────────────────────────*/
  function run() {
    optimizeVideos();
    optimizeImages();
    optimizeIframes();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }

})();